import { ChangeEvent, ReactElement, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import StoreyImagePdfPageDialog from '@editor/components/StoreyImageUpload/StoreyImagePdfPageDialog'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import useChatFloorplanUpload, { PickedFloorplan } from '../hooks/useChatFloorplanUpload'
import { FileRequestCard } from '../utils'
import { ChangeDecision } from './DecisionButtons'
import FileRequestDecision from './FileRequestDecision'

interface Props {
  card: FileRequestCard
  decision: ChangeDecision | undefined
  /** Uploaded (at least one plan), or declined. */
  onDecide: (uploaded: boolean) => void
}

const isPdfFile = (file: File): boolean =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')

/**
 * The agent asks for the storey plans (Folien): one optional pick per
 * storey (image or a PDF page); storeys left empty get no plan. Uploaded
 * together, then the agent measures them in.
 */
const FloorplanFileRequestItem = ({ card, decision, onDecide }: Props): ReactElement => {
  // Picked files are local until "Hochladen" -- independent optional picks,
  // not form fields, so no Form2 here.
  const [picked, setPicked] = useState<Record<string, PickedFloorplan>>({})
  const [pendingPdf, setPendingPdf] = useState<{ storey: string; file: File } | null>(null)
  // Storeys whose plan is already stored (an upload of several can fail midway).
  const [uploaded, setUploaded] = useState<ReadonlySet<string>>(new Set())
  const { mutate: upload, isLoading } = useChatFloorplanUpload(
    () => onDecide(true),
    storey => setUploaded(previous => new Set(previous).add(storey)),
  )
  const pendingEntries = Object.entries(picked).filter(([storey]) => !uploaded.has(storey))

  const pick = (storey: string, value: PickedFloorplan | null) =>
    setPicked(previous => {
      const { [storey]: _, ...rest } = previous
      return value ? { ...rest, [storey]: value } : rest
    })

  const handleFileChange = (storey: string) => (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (isPdfFile(file)) setPendingPdf({ storey, file })
    else pick(storey, { file })
  }

  return (
    <Box
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
      data-cy="ai-chat-file-request-floorplans"
    >
      <Typography variant="body2">
        {card.message ?? 'Bitte wählen Sie die Grundrisse der Stockwerke aus.'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Jedes Stockwerk ist optional – ohne Datei erhält es keine Folie.
      </Typography>
      <Stack spacing={0.5} mt={1}>
        {card.storeys.map(({ storey, hasPlan }) => {
          const chosen = picked[storey]
          return (
            <Stack
              key={storey}
              direction="row"
              alignItems="center"
              spacing={1}
              data-cy={`ai-chat-file-request-storey-${storey}`}
            >
              <Typography variant="body2" sx={{ width: 56, flexShrink: 0 }}>
                {storeyToLabel(storey)}
              </Typography>
              {hasPlan ? (
                <Typography variant="caption" color="text.secondary">
                  Folie vorhanden
                </Typography>
              ) : chosen ? (
                <>
                  <Typography variant="caption" noWrap sx={{ minWidth: 0, flex: 1 }}>
                    {chosen.file.name}
                    {chosen.pdfPage !== undefined && ` (Seite ${chosen.pdfPage})`}
                  </Typography>
                  {uploaded.has(storey) ? (
                    <Typography variant="caption" color="success.main">
                      Hochgeladen
                    </Typography>
                  ) : (
                    !decision && (
                      <Tooltip title="Auswahl entfernen">
                        <IconButton
                          size="small"
                          disabled={isLoading}
                          onClick={() => pick(storey, null)}
                          data-cy={`ai-chat-file-request-storey-${storey}-remove`}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  )}
                </>
              ) : (
                !decision && (
                  <Button
                    size="small"
                    component="label"
                    disabled={isLoading}
                    startIcon={<UploadFileIcon fontSize="small" />}
                    data-cy={`ai-chat-file-request-storey-${storey}-select`}
                  >
                    Datei auswählen
                    <input
                      hidden
                      type="file"
                      accept="image/*,application/pdf,.pdf"
                      onChange={handleFileChange(storey)}
                      data-cy={`ai-chat-file-request-storey-${storey}-input`}
                    />
                  </Button>
                )
              )}
            </Stack>
          )
        })}
      </Stack>
      {decision ? (
        <FileRequestDecision decision={decision} />
      ) : (
        <Stack direction="row" spacing={1} mt={1} justifyContent="flex-end" alignItems="center">
          {isLoading && <CircularProgress size={16} />}
          <Button
            size="small"
            color="inherit"
            disabled={isLoading}
            // After a partly failed upload the stored plans count as uploaded.
            onClick={() => onDecide(uploaded.size > 0)}
            data-cy="ai-chat-file-request-skip"
          >
            {uploaded.size > 0 ? 'Ohne weitere Folien' : 'Ohne Folien'}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={isLoading || pendingEntries.length === 0}
            onClick={() => upload(pendingEntries)}
            data-cy="ai-chat-file-request-floorplans-upload"
          >
            {pendingEntries.length > 1 ? `${pendingEntries.length} Folien hochladen` : 'Hochladen'}
          </Button>
        </Stack>
      )}
      <StoreyImagePdfPageDialog
        open={pendingPdf !== null}
        file={pendingPdf?.file ?? null}
        storey={pendingPdf?.storey ?? ''}
        onConfirm={pdfPage => {
          if (pendingPdf) pick(pendingPdf.storey, { file: pendingPdf.file, pdfPage })
          setPendingPdf(null)
        }}
        onClose={() => setPendingPdf(null)}
      />
    </Box>
  )
}

export default FloorplanFileRequestItem
